import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { Modal, Checkbox } from 'antd';
import { useState } from 'react';
import styles from './DownloadsPage.module.less';
import unrealIcon from 'assets/img/unreal-logo-icon.png';
import unityIcon from 'assets/img/unity-logo-icon.png';
import githubIcon from 'assets/img/github-logo-icon.png';
import SdkEula from './SdkEula';

// SVG Download Icon
const DownloadIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
		<polyline points="7 10 12 15 17 10"></polyline>
		<line x1="12" y1="15" x2="12" y2="3"></line>
	</svg>
);

// SVG Icon for External Link (Open in New Tab)
const OpenIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
		<polyline points="15 3 21 3 21 9"></polyline>
		<line x1="10" y1="14" x2="21" y2="3"></line>
	</svg>
);

// SVG Icon for PC Applications (Desktop Monitor)
const PcIcon = ({ className }) => (
	<svg
		className={className}
		width="48"
		height="48"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
		<line x1="8" y1="21" x2="16" y2="21"></line>
		<line x1="12" y1="17" x2="12" y2="21"></line>
	</svg>
);

// SVG Icon for VR/Meta Quest (VR Headset)
const QuestIcon = ({ className }) => (
	<svg
		className={className}
		width="48"
		height="48"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		{/* Side strap attachments */}
		<path d="M2 12h2" />
		<path d="M20 12h2" />

		{/* Headset visor with rounded nose cutout */}
		<path d="M7 8h10a3 3 0 0 1 3 3v2a3 3 0 0 1-3 3h-3a2 2 0 0 0-4 0H7a3 3 0 0 1-3-3v-2a3 3 0 0 1 3-3z" />
	</svg>
);

export default function DownloadsPage() {
	const { loginWithRedirect } = useAuth0();
	const { t } = useTranslation();

	// Modal States
	const [unityModalVisible, setUnityModalVisible] = useState(false);
	const [unrealModalVisible, setUnrealModalVisible] = useState(false);
	const [sdkButtonText, setSdkButtonText] = useState('Download SDK');

	// Track which Unreal SDK version is currently selected
	const [selectedUnrealSdk, setSelectedUnrealSdk] = useState(null);

	// *** Unity Logic ***
	const [checkedUnity, setCheckedUnity] = useState(false);

	const showUnityEula = () => setUnityModalVisible(true);

	const hideUnityEula = () => {
		setUnityModalVisible(false);
		setCheckedUnity(false);
	};

	const handleCheckboxChangeUnity = () => setCheckedUnity(!checkedUnity);

	const hideUnityEulaDelay = () => {
		setSdkButtonText('Your download will begin in a moment');
		setTimeout(() => {
			setUnityModalVisible(false);
			setCheckedUnity(false);
		}, 2000);
		setTimeout(() => setSdkButtonText('Download SDK'), 2000);
	};

	// *** Unreal Logic ***
	const [checkedUnreal, setCheckedUnreal] = useState(false);

	const showUnrealEula = (sdk) => {
		setSelectedUnrealSdk(sdk);
		setUnrealModalVisible(true);
	};

	const hideUnrealEula = () => {
		setUnrealModalVisible(false);
		setSelectedUnrealSdk(null);
		setCheckedUnreal(false);
	};

	const handleCheckboxChangeUnreal = () => setCheckedUnreal(!checkedUnreal);

	const hideUnrealEulaDelay = () => {
		setSdkButtonText('Your download will begin in a moment');
		setTimeout(() => {
			setUnrealModalVisible(false);
			setSelectedUnrealSdk(null);
			setCheckedUnreal(false);
		}, 2000);
		setTimeout(() => setSdkButtonText('Download SDK'), 2000);
	};

	// Shared style for the Quick Start Guide buttons
	const guideButtonStyle = {
		backgroundColor: '#f8f9fa',
		color: '#333',
		border: '1px solid #ddd',
		padding: '8px 16px',
		borderRadius: '6px',
		textDecoration: 'none',
		fontWeight: '500',
		fontSize: '14px',
		display: 'flex',
		alignItems: 'center',
		gap: '8px',
		transition: 'background-color 0.2s',
		whiteSpace: 'nowrap',
	};

	// *** Unreal SDK Data ***
	const unrealSdks = [
		{
			version: '5.8',
			link: 'https://www.dropbox.com/scl/fi/qwkzdqs7i3t0qn14q5c83/OmniConnectSDK_UE5.8.zip?rlkey=vkmcibq5aq5l6weutejtrs3i4&st=7bb9jaei&dl=1',
		},
		{
			version: '5.7',
			link: 'https://www.dropbox.com/scl/fi/auazszgo63ial3h72g9rd/OmniConnectSDK_UE5.7.zip?rlkey=3309qivetnwgfsw7dd1sto7vl&st=vuxz4ak0&dl=1',
		},
		{
			version: '5.6',
			link: 'https://www.dropbox.com/scl/fi/jq5yndjtbs5rsolset6dg/OmniConnectSDK_UE5.6.zip?rlkey=bk1el53ej6p5qyr39ose399m2&st=1rg1tmvh&dl=1',
		},
		{
			version: '5.5',
			link: 'https://www.dropbox.com/scl/fi/t4uzgid02nrqztwwnnqca/OmniConnectSDK_UE5.5.zip?rlkey=cxv7tazunn9z26fnykutnba0x&st=cgpgi96w&dl=1',
		},
		{
			version: '5.4',
			link: 'https://www.dropbox.com/scl/fi/fjxm4tzv9rb872db4pqoc/OmniConnectSDK_UE5.4.zip?rlkey=0527e8ns9l80dclixcle54c9z&st=j3gxng4x&dl=1',
		},
		{
			version: '5.3',
			link: 'https://www.dropbox.com/scl/fi/n7455s947hp0rrqi84qyp/OmniConnectSDK_UE5.3.zip?rlkey=hu5mxix84zo4my3ewe023ud93&st=baue8y80&dl=1',
		},
		{
			version: '5.2',
			link: 'https://www.dropbox.com/scl/fi/cjxv1nqlz0oey3d3cqati/OmniConnectSDK_UE5.2.zip?rlkey=tpy6hhr7lo8m3h2scelvru1f9&st=2mi76bn4&dl=1',
		},
		{
			version: '5.1',
			link: 'https://www.dropbox.com/scl/fi/etszz84ncesagtqmrp6ky/OmniConnectSDK_UE5.1.zip?rlkey=r4d8tfcztz98321a41lm8zk7x&st=tb4cve0n&dl=1',
		},
		{
			version: '5.0',
			link: 'https://www.dropbox.com/scl/fi/yylpu7aq1avtoz3ruqxxt/OmniConnectSDK_UE5.0.zip?rlkey=40m7yt27fwinu0hp0xzoc0xwm&st=8yf673mw&dl=1',
		},
		{
			version: '4.27',
			link: 'https://www.dropbox.com/scl/fi/uk1qi0fu1dnhkjw0ngg2w/OmniConnectSDK_UE4.27.zip?rlkey=l8ca47gsl9p2naoyc15qxuw50&st=9lxbyj55&dl=1',
		},
		{
			version: '4.26',
			link: 'https://www.dropbox.com/scl/fi/onsmo33y6igjpe2kra2m2/OmniConnectSDK_UE4.26.zip?rlkey=gcielvyq328n7i36uhzrxa8x3&st=c1axtijw&dl=1',
		},
	];

	return (
		<>
			<div
				className={styles.DownloadsPageContainer}
				style={{ maxWidth: '1200px', margin: '0 auto', padding: '30px 20px 80px' }}
			>
				<h1 className={styles.DownloadsPageTitle}>Downloads</h1>

				{/* *** Omni Platform Integration Overview Section *** */}
				<section className={styles.IntegrationBanner}>
					<div className={styles.IntegrationBannerLeft}>
						<div className={styles.IntegrationBannerHeaderGroup}>
							<h2 style={{ margin: '0', fontSize: '24px', color: '#1a1d21' }}>Omni Platform Integration Overview</h2>
							<span className={`${styles.Badge} ${styles.BadgeGuide}`}>GUIDE</span>
						</div>
						<p style={{ margin: '8px 0 0 0', fontSize: '16px', fontWeight: 'normal', color: '#555' }}>
							This guide provides an overview of the Omni device integration components, supported platforms, and available
							data.
						</p>
					</div>
					<div className={styles.IntegrationBannerRight}>
						<a
							href="https://www.dropbox.com/scl/fi/70b40s3zu3vmp1awlulaa/OmniPlatformIntegrationOverview.pdf?rlkey=gmb479q3h936t87v1kbbvd0y2&st=x49xgxn0&e=1&dl=0"
							target="_blank"
							rel="noopener noreferrer"
							style={{ textDecoration: 'none' }}
						>
							<button
								className={styles.DownloadsPageButton}
								style={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									gap: '8px',
									width: 'auto',
									padding: '12px 24px',
								}}
							>
								<DownloadIcon /> Download PDF
							</button>
						</a>
					</div>
				</section>

				{/* *** Integration Support Registration Section *** */}
				<section className={styles.IntegrationBanner}>
					<div className={styles.IntegrationBannerLeft}>
						<div className={styles.IntegrationBannerHeaderGroup}>
							<h2 style={{ margin: '0', fontSize: '24px', color: '#1a1d21' }}>Register for Integration Support</h2>
							<span className={`${styles.Badge} ${styles.BadgeGuide}`}>REQUIRED</span>
						</div>
						<p style={{ margin: '8px 0 0 0', fontSize: '16px', fontWeight: 'normal', color: '#555' }}>
							Please complete this form to register for Omni integration support. For any integration-related questions,{' '}
							<span className={styles.ContactEmail}>contact enterprise-developers@virtuix.com</span>.
						</p>
					</div>
					<div className={styles.IntegrationBannerRight}>
						<a
							href="https://forms.gle/FTnta9oZWRUd9MEJ9"
							target="_blank"
							rel="noopener noreferrer"
							style={{ textDecoration: 'none' }}
						>
							<button
								className={styles.DownloadsPageButton}
								style={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									gap: '8px',
									width: 'auto',
									padding: '12px 24px',
								}}
							>
								<OpenIcon /> Open Form
							</button>
						</a>
					</div>
				</section>

				{/* *** Omni Connect Section *** */}
				<section className={styles.SectionWrapper}>
					<div
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'flex-end',
							borderBottom: '2px solid #eee',
							paddingBottom: '15px',
							marginBottom: '25px',
							flexWrap: 'wrap',
							gap: '15px',
						}}
					>
						<div>
							<div className={styles.SectionHeaderGroup}>
								<h2 style={{ margin: '0', fontSize: '24px' }}>Omni Connect</h2>
								<span className={`${styles.Badge} ${styles.BadgeApp}`}>Companion App</span>
							</div>
							<h3 style={{ margin: '8px 0 0 0', fontSize: '16px', fontWeight: 'normal', color: '#555' }}>
								The essential companion app for linking Omni One with Meta Quest, PICO, and compatible SteamVR headsets.
							</h3>
						</div>
					</div>

					<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
						{/* Windows Installer Card */}
						<div>
							<h4 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#333' }}>Desktop Installation</h4>
							<div className={styles.DownloadsPageCard}>
								<div className={styles.DownloadsPageCardIconsTop}>
									<PcIcon className={styles.DownloadsPageCardIcon} />
								</div>
								<p className={styles.DownloadsPageHeader}>Omni Connect for Windows PC</p>
								<a
									href="https://www.dropbox.com/scl/fi/tc8ocxhqrqu5vhw0ccyg7/OmniConnectSetup.exe?rlkey=88fplpt98k0y24274wd98wwnk&st=qid2vme0&dl=1"
									rel="noreferrer"
									download
									style={{ textDecoration: 'none' }}
								>
									<button className={styles.DownloadsPageButton}>Download</button>
								</a>
							</div>
						</div>

						{/* Meta Quest Card */}
						<div>
							<h4 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#333' }}>Meta Quest Automated Install</h4>
							<div className={styles.DownloadsPageCard}>
								<div className={styles.DownloadsPageCardIconsTop}>
									<QuestIcon className={styles.DownloadsPageCardIcon} />
								</div>
								<p className={styles.DownloadsPageHeader}>Omni Connect for Meta Quest (Store Link)</p>
								<a
									href="https://www.meta.com/experiences/omni-connect/26411795568507473/"
									target="_blank"
									rel="noopener noreferrer"
									style={{ textDecoration: 'none' }}
								>
									<button className={styles.DownloadsPageButton}>Get App 🡥</button>
								</a>
							</div>
						</div>

						{/* Manual Installer Card */}
						<div>
							<h4 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#333' }}>Manual / Sideloading Install</h4>
							<div className={styles.DownloadsPageCard}>
								<div className={styles.DownloadsPageCardIconsTop}>
									<QuestIcon className={styles.DownloadsPageCardIcon} />
								</div>
								<p className={styles.DownloadsPageHeader}>Omni Connect for Meta Quest/PICO (APK)</p>
								<a
									href="https://www.dropbox.com/scl/fi/4jber831unov83fp9rpgo/OmniConnectOnline_Installer.zip?rlkey=lbryqab4i1h3e50gwruc69fyh&st=xtu76yz2&dl=1"
									rel="noreferrer"
									download
									style={{ textDecoration: 'none' }}
								>
									<button className={styles.DownloadsPageButton}>Download</button>
								</a>
							</div>
						</div>
					</div>
				</section>

				{/* *** Omni First Steps Section *** */}
				<section className={styles.SectionWrapper}>
					<div
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'flex-end',
							borderBottom: '2px solid #eee',
							paddingBottom: '15px',
							marginBottom: '25px',
							flexWrap: 'wrap',
							gap: '15px',
						}}
					>
						<div>
							<div className={styles.SectionHeaderGroup}>
								<h2 style={{ margin: '0', fontSize: '24px' }}>Omni First Steps</h2>
								<span className={`${styles.Badge} ${styles.BadgeApp}`}>Sample Game</span>
							</div>
							<h3 style={{ margin: '8px 0 0 0', fontSize: '16px', fontWeight: 'normal', color: '#555' }}>
								Experience Omni One with this interactive sample game, serving as an introduction to the system.
							</h3>
						</div>
					</div>

					<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
						{/* Windows PC Card */}
						<div>
							<h4 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#333' }}>Desktop Installation</h4>
							<div className={styles.DownloadsPageCard}>
								<div className={styles.DownloadsPageCardIconsTop}>
									<PcIcon className={styles.DownloadsPageCardIcon} />
								</div>
								<p className={styles.DownloadsPageHeader}>Omni First Steps for Windows PC</p>
								<a
									href="https://www.dropbox.com/scl/fi/39zci8qbl7ud6tjao35nt/OmniFirstSteps_PC.zip?rlkey=my92ku1rc8ubhgnpgjouhcesh&st=aitfvu9z&dl=1"
									rel="noreferrer"
									download
									style={{ textDecoration: 'none' }}
								>
									<button className={styles.DownloadsPageButton}>Download</button>
								</a>
							</div>
						</div>

						{/* Meta Quest Card */}
						<div>
							<h4 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#333' }}>Meta Quest Automated Install</h4>
							<div className={styles.DownloadsPageCard}>
								<div className={styles.DownloadsPageCardIconsTop}>
									<QuestIcon className={styles.DownloadsPageCardIcon} />
								</div>
								<p className={styles.DownloadsPageHeader}>Omni First Steps for Meta Quest (Store Link)</p>
								<a
									href="https://www.meta.com/experiences/omni-first-steps/1111410428724467/"
									target="_blank"
									rel="noopener noreferrer"
									style={{ textDecoration: 'none' }}
								>
									<button className={styles.DownloadsPageButton}>Get App 🡥</button>
								</a>
							</div>
						</div>

						{/* Manual Installer Card with Dual Buttons */}
						<div>
							<h4 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#333' }}>Manual / Sideloading Install</h4>
							<div className={styles.DownloadsPageCard}>
								<div className={styles.DownloadsPageCardIconsTop}>
									<QuestIcon className={styles.DownloadsPageCardIcon} />
								</div>

								<div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
									{/* Meta Quest Manual Download */}
									<div>
										<p className={styles.DownloadsPageHeader} style={{ margin: '0 0 10px 0' }}>
											Omni First Steps for Meta Quest (APK)
										</p>
										<a
											href="https://www.dropbox.com/scl/fi/knsq4vr45ldtq9bfgct6w/OmniFirstSteps_Quest_Installer.zip?rlkey=0f3nkhsdijz7e7k2wghb11cwi&st=iabul3pq&dl=1"
											rel="noreferrer"
											download
											style={{ textDecoration: 'none' }}
										>
											<button className={styles.DownloadsPageButton}>Download</button>
										</a>
									</div>

									{/* PICO Manual Download */}
									<div>
										<p className={styles.DownloadsPageHeader} style={{ margin: '10px 0 10px 0' }}>
											Omni First Steps for PICO (APK)
										</p>
										<a
											href="https://www.dropbox.com/scl/fi/obbs588nwfey97wnud0i4/OmniFirstSteps_Pico_Installer.zip?rlkey=w8iog5polzhkn5f8t01pkdtsz&st=bgdhdcna&dl=1"
											rel="noreferrer"
											download
											style={{ textDecoration: 'none' }}
										>
											<button className={styles.DownloadsPageButton}>Download</button>
										</a>
									</div>
								</div>
							</div>
						</div>
					</div>
				</section>

				{/* *** Unity Section *** */}
				<section className={styles.SectionWrapper}>
					<div
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'flex-end',
							borderBottom: '2px solid #eee',
							paddingBottom: '15px',
							marginBottom: '25px',
							flexWrap: 'wrap',
							gap: '15px',
						}}
					>
						<div>
							<div className={styles.SectionHeaderGroup}>
								<h2 style={{ margin: '0', fontSize: '24px' }}>Unity SDK</h2>
								<span className={`${styles.Badge} ${styles.BadgeSdk}`}>Developer SDK</span>
							</div>
							<h3 style={{ margin: '8px 0 0 0', fontSize: '16px', fontWeight: 'normal', color: '#555' }}>
								Bring your Unity content to Omni One Enterprise.
							</h3>
						</div>

						<div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
							<a
								href="https://www.dropbox.com/scl/fi/u9886yobpe7wtfgxmd5gd/OmniConnectSDKSetup_Unity.pdf?rlkey=g0a4v58qjq7rqaucsp5y5mkfq&st=l32ht74j&dl=1"
								target="_blank"
								rel="noopener noreferrer"
								style={guideButtonStyle}
							>
								<DownloadIcon /> PC Unity SDK Guide 📄
							</a>
							<a
								href="https://www.dropbox.com/scl/fi/hl1tk50lz442snlxf5b6r/OmniConnectSDKSetup_Enterprise_Quest_Unity.pdf?rlkey=k3edc1rah668ci50bcaihvou2&st=43qisc2e&dl=1"
								target="_blank"
								rel="noopener noreferrer"
								style={guideButtonStyle}
							>
								<DownloadIcon /> Meta Quest / PICO Unity SDK Guide 📄
							</a>
						</div>
					</div>

					<div className={styles.DownloadsPageUnityContainer}>
						<div className={styles.DownloadsPageCard}>
							<div className={styles.DownloadsPageCardIconsTop}>
								<img src={unityIcon} className={styles.DownloadsPageCardIcon} alt="Unity" />
							</div>
							<p className={styles.DownloadsPageHeader}>Omni Connect SDK for Unity</p>
							<button className={styles.DownloadsPageButton} onClick={showUnityEula}>
								Download
							</button>
						</div>
					</div>
				</section>

				{/* *** Unreal Engine Section *** */}
				<section className={styles.SectionWrapper}>
					<div
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'flex-end',
							borderBottom: '2px solid #eee',
							paddingBottom: '15px',
							marginBottom: '25px',
							flexWrap: 'wrap',
							gap: '15px',
						}}
					>
						<div>
							<div className={styles.SectionHeaderGroup}>
								<h2 style={{ margin: '0', fontSize: '24px' }}>Unreal Engine SDK</h2>
								<span className={`${styles.Badge} ${styles.BadgeSdk}`}>Developer SDK</span>
							</div>
							<h3 style={{ margin: '8px 0 0 0', fontSize: '16px', fontWeight: 'normal', color: '#555' }}>
								Bring your Unreal content to Omni One Enterprise.
							</h3>
						</div>

						<div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
							<a
								href="https://www.dropbox.com/scl/fi/u9qh6qghx4fmu0o90cq85/OmniConnectSDKSetup_Unreal.pdf?rlkey=qa5lv56w58d0o0zbzhf4vw45d&st=wiwgodbl&dl=1"
								target="_blank"
								rel="noopener noreferrer"
								style={guideButtonStyle}
							>
								<DownloadIcon /> PC Unreal SDK Guide 📄
							</a>
							<a
								href="https://www.dropbox.com/scl/fi/9vdzolb0pbbxt93s8b4wu/OmniConnectSDKSetup_Enterprise_Quest_Unreal.pdf?rlkey=b6y5xj7uskasksbh9h7zs9i6e&st=z9et4iv3&dl=1"
								target="_blank"
								rel="noopener noreferrer"
								style={guideButtonStyle}
							>
								<DownloadIcon /> Meta Quest / PICO Unreal SDK Guide 📄
							</a>
						</div>
					</div>

					<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
						{unrealSdks.map((sdk, index) => (
							<div key={index} className={styles.DownloadsPageCard}>
								<div className={styles.DownloadsPageCardIconsTop}>
									<img src={unrealIcon} className={styles.DownloadsPageCardIcon} alt="Unreal" />
								</div>
								<p className={styles.DownloadsPageHeader}>Omni Connect SDK for Unreal {sdk.version}</p>
								<button className={styles.DownloadsPageButton} onClick={() => showUnrealEula(sdk)}>
									Download
								</button>
							</div>
						))}
					</div>
				</section>
			</div>

			{/* *** Unity Modal *** */}
			<Modal
				title="Omni Connect SDK - Read and Accept Terms"
				width={960}
				visible={unityModalVisible}
				footer={null}
				onCancel={hideUnityEula}
			>
				<SdkEula />
				<Checkbox checked={checkedUnity} onChange={handleCheckboxChangeUnity}>
					I agree to the terms and conditions
				</Checkbox>
				<br />
				<a
					href="https://www.dropbox.com/scl/fi/3ujhcnj8jmndi44o47pra/com.virtuix.omniconnectsdk.zip?rlkey=n5ul7sss9h01v6l8gt4wc7gs6&st=c645xu2o&dl=1"
					rel="noreferrer"
					download
				>
					<button className={styles.DownloadsPageButtonSdk} disabled={!checkedUnity} onClick={hideUnityEulaDelay}>
						{sdkButtonText}
					</button>
				</a>
			</Modal>

			{/* *** Unreal Modal *** */}
			<Modal
				title={`Omni Connect SDK (Unreal ${selectedUnrealSdk?.version || ''}) - Read and Accept Terms`}
				width={960}
				visible={unrealModalVisible}
				footer={null}
				onCancel={hideUnrealEula}
			>
				<SdkEula />
				<Checkbox checked={checkedUnreal} onChange={handleCheckboxChangeUnreal}>
					I agree to the terms and conditions
				</Checkbox>
				<br />
				{selectedUnrealSdk && (
					<a href={selectedUnrealSdk.link} rel="noreferrer" download>
						<button className={styles.DownloadsPageButtonSdk} disabled={!checkedUnreal} onClick={hideUnrealEulaDelay}>
							{sdkButtonText}
						</button>
					</a>
				)}
			</Modal>
		</>
	);
}
